import { useState } from 'react';
import { Sparkles, Wand2 } from 'lucide-react';
import { EntityModal } from '../common';
import { useAiSuggestSeats } from '../../hooks/useFloorPlans';
import type { AiSeatingResult } from '../../api/floor-plans';

export interface AiSeatingModalProps {
  open: boolean;
  onClose: () => void;
  floorPlanId: string;
  /** Shows the suggestion on the seating board to adjust and apply there. */
  onPreview?: (result: AiSeatingResult) => void;
}

/**
 * AI-assisted seating: asks the model to arrange confirmed guests across the
 * plan's tables using richer signals (VIP, keep-together, notes) than the
 * deterministic auto-assign. Same preview → apply flow; the server validates
 * the model output so a suggestion can never overflow a table.
 */
export function AiSeatingModal({ open, onClose, floorPlanId, onPreview }: AiSeatingModalProps) {
  const [preview, setPreview] = useState<AiSeatingResult | null>(null);
  const mutation = useAiSuggestSeats(floorPlanId);

  async function run(dryRun: boolean) {
    if (dryRun) setPreview(null);
    try {
      // On Apply, send back the reviewed arrangement so the persisted plan
      // matches the preview rather than a freshly generated one.
      const reviewedAssignments =
        !dryRun && preview
          ? preview.assignments.map((a) => ({ guestId: a.guestId, tableNumber: a.tableNumber }))
          : undefined;
      const result = await mutation.mutateAsync({ dryRun, reviewedAssignments });
      if (dryRun && onPreview) {
        onPreview(result);
        onClose();
        return;
      }
      setPreview(result);
      if (!dryRun && result.applied && result.unassigned.length === 0) {
        setTimeout(() => onClose(), 600);
      }
    } catch {
      // surfaced via mutation.error
    }
  }

  return (
    <EntityModal open={open} onClose={onClose} title="AI Seating Suggestion" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The AI planner groups your confirmed guests by family, side, and VIP status — honoring
          "keep together" preferences and any guest notes — then proposes a table for each party.
          Preview first; nothing is saved until you apply.
        </p>

        {preview && (
          <section
            className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2"
            data-testid="ai-preview-result"
          >
            <div className="text-sm font-medium text-gray-900">
              {preview.applied ? 'Applied' : 'Preview'}
            </div>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                Will seat: <strong>{preview.assignments.length}</strong> party record(s)
              </li>
              {preview.unassigned.length > 0 && (
                <li className="text-amber-700">
                  Unseated: <strong>{preview.unassigned.length}</strong>
                </li>
              )}
            </ul>
            {preview.rationale && (
              <p className="text-xs text-gray-600 italic" data-testid="ai-rationale">
                “{preview.rationale}”
              </p>
            )}
            {preview.warnings.length > 0 && (
              <ul className="text-xs text-amber-700 list-disc list-inside">
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {mutation.error && (
          <p className="text-sm text-red-600" data-testid="ai-seating-error">
            {mutation.error.message}
          </p>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => run(true)}
          disabled={mutation.isPending}
          className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50"
          data-testid="ai-seating-preview"
        >
          {mutation.isPending && preview === null
            ? 'Thinking…'
            : onPreview
              ? 'Preview on the board'
              : 'Preview'}
        </button>
        <button
          type="button"
          onClick={() => run(false)}
          disabled={mutation.isPending || preview === null}
          className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="ai-seating-apply"
        >
          <Sparkles size={14} />
          {mutation.isPending && preview !== null ? 'Applying…' : 'Apply'}
        </button>
      </div>
    </EntityModal>
  );
}

/** Toolbar button that opens the AI seating modal. */
export function AiSeatingButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      data-testid="ai-seating-btn"
    >
      <Wand2 size={16} className="text-primary-600" />
      AI Suggest
    </button>
  );
}
